import { toast } from "react-toastify";
import { Box } from "@mui/material";
import { useMe } from "../../../auth/MeContext";
import { PERM } from "../../../auth/permissions";
import { Button, Form, Input, Modal, Space, Switch, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../../axios/axios-instance";
import { pathAPI } from "../../../utils/pathApi";

interface LanguageOption {
  id: number;
  name: string;
  language_code: string;
}

interface Province {
  id: number;
  name: string;
  active?: boolean;
  translations?: {
    language_id: number;
    name: string;
  }[];
}

interface LocationModalProps {
  open: boolean;
  onClose: () => void;
  countryId?: number;
  countryName?: string;
  languageCode: string;
}

const LocalModal: React.FC<LocationModalProps> = ({ open, onClose, countryId, countryName, languageCode }) => {
  const { t } = useTranslation();
  const { can } = useMe();
  const canCreate = can(PERM.COUNTRY_CREATE);
  const canUpdate = can(PERM.COUNTRY_UPDATE);
  const canDelete = can(PERM.COUNTRY_DELETE);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [loadingProvinces, setLoadingProvinces] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingActive, setEditingActive] = useState(true);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [editingTranslations, setEditingTranslations] = useState<{ [key: number]: string }>({});

  // Fetch languages
  const fetchLanguages = async () => {
    try {
      const response = await axiosInstance.get(pathAPI.languages);
      if (response.data?.data) {
        setLanguages(response.data.data);
      }
    } catch (error) {
      toast.error(t('country.province.lang_error'));
    }
  };

  useEffect(() => {
    if (open) {
      fetchLanguages();
      if (countryId) {
        fetchProvinces();
      }
    }
  }, [open, countryId]);

  const fetchProvinces = async () => {
    if (!countryId) return;

    try {
      setLoadingProvinces(true);
      const response = await axiosInstance.get(`${pathAPI.cities.replace(":slug", countryId.toString())}`);
      setProvinces(response.data.data);
    } catch (error) {
      toast.error(t('country.province.fetch_error'));
    } finally {
      setLoadingProvinces(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setEditingId(null);
    setEditingName('');
    setEditingActive(true);
    setEditingTranslations({});
    onClose();
  };

  const handleSubmit = async (values: any) => {
    if (!countryId) {
      toast.error(t('country.province.country_required'));
      return;
    }

    setLoading(true);
      await axiosInstance.post(`${pathAPI.countries}/${countryId}/cities`, {
        province_name: values.provinceName,
        active: values.active ?? true,
      }).then(() => {
        toast.success(t('country.province.added'));
        form.resetFields();
      }).catch((err) => {
        toast.error(t('country.province.add_error'));
      }).finally(() => {
        setLoading(false);
      });
  };

  const handleEdit = (record: Province) => {
    setEditingId(record.id);
    setEditingName(record.name);
    setEditingActive(record.active ?? true);

    // Initialize translations
    const translations: { [key: number]: string } = {};
    record.translations?.forEach(trans => {
      translations[trans.language_id] = trans.name;
    });
    setEditingTranslations(translations);
  };

  const handleSave = async (id: number) => {
    try {
      setLoading(true);
      const translations = Object.entries(editingTranslations).map(([langId, name]) => ({
        language_id: parseInt(langId),
        name
      }));

      await axiosInstance.put(`${pathAPI.countries}/${countryId}/cities/${id}`, {
        name: editingName,
        active: editingActive,
        translations
      });

      toast.success(t('country.province.updated'));
      setEditingId(null);
      setEditingName('');
      setEditingActive(true);
      setEditingTranslations({});
      fetchProvinces();
    } catch (error) {
      toast.error(t('country.province.update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleTranslationChange = (langId: number, value: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [langId]: value
    }));
  };

  const handleDeleteProvince = async (id: number) => {
    try {
      setLoading(true);
      await axiosInstance.delete(`${pathAPI.countries}/${countryId}/cities/${id}`);
      toast.success(t('country.province.deleted'));
      fetchProvinces();
    } catch (error) {
      toast.error(t('country.province.delete_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDiscard = () => {
    setEditingId(null);
    setEditingName('');
    setEditingActive(true);
    setEditingTranslations({});
  };

  const columns = [
    {
      title: t('country.province.col.id'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center' as const,
    },
    {
      title: t('country.province.col.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Province) => {
        if (editingId === record.id) {
          return (
            <Input
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              style={{ width: '100%' }}
            />
          );
        }
        return text;
      },
    },
    {
      title: t('country.province.col.translations'),
      key: 'translations',
      render: (_: unknown, record: Province) => {
        if (editingId === record.id) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {languages.map(lang => (
                <Input
                  key={lang.id}
                  placeholder={t('country.province.lang_translation', { lang: lang.name })}
                  value={editingTranslations[lang.id] || ''}
                  onChange={(e) => handleTranslationChange(lang.id, e.target.value)}
                />
              ))}
            </div>
          );
        }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {record.translations?.map(trans => {
              const lang = languages.find(l => l.id === trans.language_id);
              return lang ? (
                <div key={lang.id} style={{ fontSize: '0.9em', color: 'var(--app-text-muted)' }}>
                  {lang.name}: {trans.name}
                </div>
              ) : null;
            })}
          </div>
        );
      },
    },
    {
      title: t('country.province.col.status'),
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center' as const,
      render: (active: boolean, record: Province) => {
        if (editingId === record.id) {
          return (
            <Switch
              checked={editingActive}
              onChange={setEditingActive}
            />
          );
        }
        return (
          <Tag color={active ? 'success' : 'error'}>
            {active ? t('country.province.active') : t('country.province.inactive')}
          </Tag>
        );
      },
    },
    {
      title: t('country.province.col.actions'),
      key: 'actions',
      width: 200,
      render: (_: any, record: Province) => (
        <>
          {editingId === record.id ? (
            <Space>
              <Button type="primary" onClick={() => handleSave(record.id)} loading={loading}>
                {t('country.province.save')}
              </Button>
              <Button onClick={handleDiscard}>{t('btn.cancel')}</Button>
            </Space>
          ) : (
            <Space>
              <Button type="primary" disabled={!canUpdate} onClick={() => handleEdit(record)}>
                {t('country.province.edit')}
              </Button>
              <Button danger disabled={!canDelete} onClick={() => handleDeleteProvince(record.id)}>
                {t('country.province.delete')}
              </Button>
            </Space>
          )}
        </>
      ),
    },
  ];

  return (
    <Modal
      title={t('country.province.title', { name: countryName })}
      open={open}
      onCancel={handleClose}
      width={800}
      footer={null}
    >
      <Box sx={{ mt: 2 }}>
        {/* Add New Province Section */}
        {canCreate && (
        <Box sx={{
          p: 2,
          mb: 3,
          border: '1px solid var(--app-border-light)',
          borderRadius: '8px',
          backgroundColor: 'var(--app-bg)'
        }}>
          <h3 style={{ marginBottom: 16 }}>{t('country.province.add_section')}</h3>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{ active: true }}
          >
            <Form.Item
              name="provinceName"
              label={t('country.province.name_label')}
              rules={[{ required: true, message: t('country.province.name_required') }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              name="active"
              label={t('country.province.col.status')}
              valuePropName="checked"
              initialValue={true}
            >
              <Switch />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('country.province.add_button')}
              </Button>
              <Button onClick={handleClose} style={{ marginLeft: 8 }}>
                {t('btn.cancel')}
              </Button>
            </Form.Item>
          </Form>
        </Box>
        )}

        {/* Provinces List Section */}
        <Box>
          <h3 style={{ marginBottom: 16 }}>{t('country.province.list_section')}</h3>
          <Table
            columns={columns}
            dataSource={provinces}
            rowKey="id"
            loading={loadingProvinces}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => t('country.province.total', { total })
            }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default LocalModal;
